import { useTranslation } from 'react-i18next'
import type { LostListItem } from '../lib/lostApi'
import { buildReportHeadline } from '../lib/reportHeadline'
import { useAuth } from '../auth/AuthContext'
import { EditableNotes } from './EditableNotes'
import { LinkedText } from './LinkedText'
import { MarkLostFoundButton } from './MarkLostFoundButton'
import { ModeratorDeleteButton } from './ModeratorDeleteButton'
import { ProfileDetailWithGallery } from './ProfileDetailWithGallery'
import { ReportMessageForm } from './ReportMessageForm'
import { ReportTranslation } from './ReportTranslation'
import {
  deleteLostReport,
  markLostReportFound,
  updateLostNotes,
} from '../lib/lostApi'

interface Props {
  item: LostListItem
  images: string[]
  onClose: () => void
  onDeleted?: () => void
  onFound?: () => void
  onUpdated?: (item: LostListItem) => void
}

function formatWhen(value: string | null, locale: string) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export function LostProfileDock({
  item,
  images,
  onClose,
  onDeleted,
  onFound,
  onUpdated,
}: Props) {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const locale = i18n.resolvedLanguage || 'en'
  const canEditNotes =
    Boolean(user) &&
    (user!.id === item.user_id || user!.moderator || user!.admin)
  const animal = item.animal
  const colors = [animal.color1, animal.color2, animal.color3].filter(
    Boolean,
  ) as string[]
  const noticed = formatWhen(item.missing_noticed_at, locale)
  const hasLocation =
    item.last_known_lat != null && item.last_known_lng != null
  const locationLabel = !hasLocation
    ? null
    : item.location_fuzzed
      ? t('location.approximate')
      : `${item.last_known_lat!.toFixed(5)}, ${item.last_known_lng!.toFixed(5)}`
  const title = buildReportHeadline(
    {
      kind: 'lost',
      type: animal.type,
      gender: animal.gender,
      spayed: animal.spayed,
      chip_status: animal.chip_status,
      temperament: item.temperament,
      missingSince: item.missing_noticed_at,
    },
    t,
    locale,
  )

  return (
    <section className="profile-dock" aria-label={title}>
      <div className="panel-header panel-header-row">
        <h2 className="panel-title panel-title-summary">{title}</h2>
        <button
          type="button"
          className="btn btn-ghost panel-close"
          onClick={onClose}
        >
          {t('panel.closePanel')}
        </button>
      </div>
      <div className="panel-body profile-dock-body">
        <ProfileDetailWithGallery
          imageKeys={images}
          alt={t('profile.lostPhoto', { id: animal.id })}
        >
          <dl className="profile-detail-grid">
            <div>
              <dt>{t('fields.type')}</dt>
              <dd>{t(`types.${animal.type}`)}</dd>
            </div>
            <div>
              <dt>{t('fields.gender')}</dt>
              <dd>{t(`gender.${animal.gender ?? 'unknown'}`)}</dd>
            </div>
            <div>
              <dt>{t('fields.fixed')}</dt>
              <dd>
                {animal.spayed === 1
                  ? t('fixed.yes')
                  : animal.spayed === 0
                    ? t('fixed.no')
                    : t('fixed.unknown')}
              </dd>
            </div>
            <div>
              <dt>{t('fields.color')}</dt>
              <dd>
                {colors.map((key) => t(`colors.${key}`)).join(' / ') ||
                  t('fields.unknown')}
              </dd>
            </div>
            <div>
              <dt>{t('fields.temperament')}</dt>
              <dd>{t(`temperament.${item.temperament ?? 'not_sure'}`)}</dd>
            </div>
            <div>
              <dt>{t('fields.chip')}</dt>
              <dd>
                {animal.chip_status === 'known' && animal.chip
                  ? animal.chip
                  : t(`chip.${animal.chip_status ?? 'unknown'}`)}
              </dd>
            </div>
            {noticed && (
              <div>
                <dt>{t('lost.noticedAt')}</dt>
                <dd>{noticed}</dd>
              </div>
            )}
            {locationLabel && (
              <div>
                <dt>{t('lost.lastKnownLocation')}</dt>
                <dd>{locationLabel}</dd>
              </div>
            )}
          </dl>

          <ReportTranslation
            entryKey={`lost-${item.id}`}
            sourceLocale={item.source_locale}
            fields={{
              contact_info: item.contact_info,
              if_found_contact:
                animal.if_found_contact &&
                animal.if_found_contact !== item.contact_info
                  ? animal.if_found_contact
                  : null,
              notes: canEditNotes ? null : item.notes,
            }}
          >
            {(text) => (
              <dl className="profile-detail-grid">
                {text.contact_info && (
                  <div className="profile-detail-wide">
                    <dt>{t('fields.contact')}</dt>
                    <dd>
                      <LinkedText text={text.contact_info} />
                    </dd>
                  </div>
                )}
                {text.if_found_contact && (
                  <div className="profile-detail-wide">
                    <dt>{t('fields.ifFound')}</dt>
                    <dd>
                      <LinkedText text={text.if_found_contact} />
                    </dd>
                  </div>
                )}
                {text.notes && (
                  <div className="profile-detail-wide">
                    <dt>{t('fields.notes')}</dt>
                    <dd>
                      <LinkedText text={text.notes} />
                    </dd>
                  </div>
                )}
              </dl>
            )}
          </ReportTranslation>

          {canEditNotes && (
            <EditableNotes
              ownerUserId={item.user_id}
              value={item.notes}
              onSave={async (notes) => {
                const updated = await updateLostNotes(
                  item.id,
                  notes,
                  i18n.resolvedLanguage || i18n.language || 'en',
                )
                onUpdated?.(updated)
              }}
            />
          )}

          <MarkLostFoundButton
            ownerUserId={item.user_id}
            onMarkFound={async () => {
              await markLostReportFound(item.id)
              onFound?.()
            }}
          />

          <ModeratorDeleteButton
            onDelete={async () => {
              await deleteLostReport(item.id)
              onDeleted?.()
            }}
          />
        </ProfileDetailWithGallery>

        <ReportMessageForm
          kind="lost"
          reportId={item.id}
          ownerUserId={item.user_id}
        />
      </div>
    </section>
  )
}
